import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HelpCenterPage.css';

const HelpCenterPage = () => {
  const navigate = useNavigate();

  const scrollToSection = (id) => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Topics', icon: 'help_outline' },
    { id: 'getting-started', name: 'Getting Started', icon: 'play_circle_outline' },
    { id: 'account', name: 'Account & Billing', icon: 'account_circle' },
    { id: 'features', name: 'Features', icon: 'star' },
    { id: 'technical', name: 'Technical Support', icon: 'build' }
  ];

  const articles = [
    {
      id: 1,
      title: 'How do I get started with Nativelike?',
      category: 'getting-started',
      content: 'Getting started is easy! Simply sign up for a free account, choose your educational level, and start learning vocabulary with our AI-powered system.'
    },
    {
      id: 2,
      title: 'What educational levels are available?',
      category: 'features',
      content: 'We offer vocabulary organized by Primary, Middle, and High School levels, all aligned with CEFR standards and school curricula.'
    },
    {
      id: 3,
      title: 'How does the spaced repetition system work?',
      category: 'features',
      content: 'Our AI-powered spaced repetition algorithm shows you vocabulary at optimal intervals to maximize retention. Words you struggle with appear more frequently until you master them.'
    },
    {
      id: 4,
      title: 'Can I cancel my subscription anytime?',
      category: 'account',
      content: 'Yes! You can cancel your subscription at any time from your account settings. Your access will continue until the end of your current billing period.'
    },
    {
      id: 5,
      title: 'How do I track my progress?',
      category: 'features',
      content: 'Visit your dashboard to see detailed analytics including vocabulary learned, reading progress, and improvement over time. All data is visualized in easy-to-understand charts.'
    },
    {
      id: 6,
      title: 'What browsers are supported?',
      category: 'technical',
      content: 'Syllera works best on Chrome, Firefox, Safari, and Edge. Make sure you have the latest version of your browser for the best experience.'
    },
    {
      id: 7,
      title: 'How do I reset my password?',
      category: 'account',
      content: 'Click on "Forgot Password" on the login page, enter your email, and you\'ll receive instructions to reset your password.'
    },
    {
      id: 8,
      title: 'Are there mobile apps available?',
      category: 'technical',
      content: 'Currently, Syllera is available on web browsers. Our web version is fully responsive and works great on mobile devices. Mobile apps are coming soon!'
    }
  ];

  const filteredArticles = articles.filter(article => {
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="help-center-page">
      {/* Navigation Bar */}
      <nav className="landing-navbar">
        <div className="navbar-container">
          <div className="logo" onClick={() => navigate('/')}>
            <span className="material-icons">school</span>
            <span>Syllera</span>
          </div>
          <div className="nav-menu">
            <a href="/#features" onClick={(e) => { e.preventDefault(); navigate('/#features'); }}>Features</a>
            <a href="/#how-it-works" onClick={(e) => { e.preventDefault(); navigate('/#how-it-works'); }}>How It Works</a>
            <a href="/#pricing" onClick={(e) => { e.preventDefault(); navigate('/#pricing'); }}>Pricing</a>
            <a href="/#testimonials" onClick={(e) => { e.preventDefault(); navigate('/#testimonials'); }}>Reviews</a>
          </div>
          <div className="nav-actions">
            <button className="btn-login" onClick={() => navigate('/#login')}>Login</button>
            <button className="btn-primary" onClick={() => navigate('/signup')}>Start Learning Free</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="help-hero">
        <div className="help-hero-content">
          <h1>Help Center</h1>
          <p>Find answers to common questions and get support</p>
          <div className="help-search">
            <span className="material-icons">search</span>
            <input
              type="text"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="help-categories">
        <div className="section-container">
          <div className="categories-grid">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`category-card ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span className="material-icons">{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="help-articles">
        <div className="section-container">
          <h2 className="section-title">
            {selectedCategory === 'all' ? 'All Articles' : categories.find(c => c.id === selectedCategory)?.name}
          </h2>
          <div className="articles-list">
            {filteredArticles.length > 0 ? (
              filteredArticles.map((article) => (
                <div key={article.id} className="article-card">
                  <h3 className="article-title">{article.title}</h3>
                  <p className="article-content">{article.content}</p>
                </div>
              ))
            ) : (
              <div className="no-results">
                <span className="material-icons">search_off</span>
                <p>No articles found. Try a different search term or category.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="help-contact">
        <div className="section-container">
          <div className="help-contact-card">
            <span className="material-icons">support_agent</span>
            <h2>Still need help?</h2>
            <p>Can't find what you're looking for? Our support team is here to help.</p>
            <button className="btn-primary" onClick={() => navigate('/contact-support')}>
              Contact Support
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenterPage;

